<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.internal.places.places_dialog.title_edit') }}
    </h1>
    <div v-loading="!isDataLoaded">
      <el-form
        v-if="isDataLoaded"
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="submitForm"
      >
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.short_name')"
            :error="errors?.short_name?.[0]"
            :class="form.short_name ? 'has-value' : ''"
            prop="short_name"
          >
            <el-input
              v-model="form.short_name"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.street')"
            :error="errors?.address_street?.[0]"
            :class="form.address_street ? 'has-value' : ''"
            prop="address_street"
            class="tw-flex-3"
          >
            <el-input
              v-model="form.address_street"
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.house_number')"
            :error="errors?.address_house_number?.[0]"
            :class="form.address_house_number ? 'has-value' : ''"
            prop="address_house_number"
            class="tw-flex-1"
          >
            <el-input
              v-model="form.address_house_number"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.city')"
            :error="errors?.address_city?.[0]"
            :class="form.address_city ? 'has-value' : ''"
            prop="address_city"
            class="tw-flex-3"
          >
            <el-input
              v-model="form.address_city"
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.zip')"
            :error="errors?.address_zip?.[0]"
            :class="form.address_zip ? 'has-value' : ''"
            prop="address_zip"
            class="tw-flex-1"
          >
            <el-input
              v-model="form.address_zip"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.country')"
            :class="form.address_country_id ? 'has-value' : ''"
            prop="address_country_id"
          >
            <el-select
              v-model="form.address_country_id"
              placeholder=""
              filterable
              :filter-method="countriesFilterMethod"
              :visible-change="handleCountriesVisibleChange"
            >
              <el-option
                v-for="item in filteredCountries"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.google_calendar_id')"
            :class="form.google_calendar_id ? 'has-value' : ''"
            prop="google_calendar_id"
          >
            <el-input
              v-model="form.google_calendar_id"
            />
          </i-form-item>
        </div>
      </el-form>
    </div>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.edit') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { deepDiff, getValidationError } from '@/helpers';
import { countriesFilterMixin } from '@/mixins/countriesFilterMixin';

const FORM_BASE = {
  short_name: '', // required, maxLength: 64
  address_street: '', //  maxLength: 128
  address_house_number: '',// required maxLength: 128
  address_city: '',// required maxLength: 128
  address_zip: '',// required maxLength: 16
  address_country_id: '', // required
  google_calendar_id: '', // required, but if entered maxLength: 32
};

export default {
  name: 'EditInternalPlaceDialog',
  mixins: [countriesFilterMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    placeId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      isDataLoaded: false,
      place: {},
      errors: {},
      form: _cloneDeep(FORM_BASE),
      initialForm: {},
      formRules: {
        short_name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 64, message: 'Max 64 characters', trigger: 'blur' },
        ],
        address_street: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        address_house_number: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        address_city: [
          { required: true, message: 'Required', trigger: 'blur'  },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        address_zip: [
          { required: true, message: 'Required', trigger: 'blur'  },
          { max: 16, message: 'Max 128 characters', trigger: 'blur' },
        ],
        address_country_id: [
          { required: true, message: 'Required', trigger: 'blur'  },
        ],
        google_calendar_id: [
          { required: true, message: 'Required', trigger: 'blur'  },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      countries: (state) => state.v2.shared.enums.enums.countries,
    }),
  },

  watch: {
    place() {
      this.setInitialFormData();
    },
    isOpen(val) {
      if (val) {
        this.loadPlaceDetails();
      }
    },
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.$refs.Form?.clearValidate();
    },

    async loadPlaceDetails() {
      this.isDataLoaded = false;
      this.place = await this.$store.dispatch('v2/admin/cms/internal/places/getPlaceDetails', {
        id: this.placeId,
      });
      this.isDataLoaded = true;
    },

    setInitialFormData() {
      const companyKeys = Object.keys(this.place);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (companyKeys.includes(key)) {
          this.form[key] = this.place[key];
          this.initialForm[key] = this.place[key];
        }
      });

      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/internal/places/editPlace', {
          id: this.place.id,
          place: deepDiff(this.initialForm, this.form),
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
